import axios from 'axios'

import { failingTestLabels, pythonErrorName } from '../component/session/testResults'
import { axiosInstance } from './axiosManager'

// Sends a student's AI chat turn. Both student chat surfaces (the Vizmental AIChat and the
// /workspace ChatContext) go through here.
//
// POST /gpt/studentChat carries only the sessionId and the conversation; the backend looks
// up the session's AI Chat Policy itself and adds it to the prompt. The policy never
// reaches the browser.
//
// Fallback: a backend deployed before that route answers 404. Rather than breaking chat for
// every student during a staggered deploy, fall back to the original /gpt/openAI route.
// (A backend that old has no policy to apply.) Only a 404 falls back - any other failure
// is a real error and is rethrown to the caller's existing error handling.
//
// A classroom-study condition (workspace/study/studyConfig.js) also sends who is asking and
// their latest run (studyState); the server builds that tutor's whole prompt and logs every
// exchange. It never falls back: /gpt/openAI has no study tutor, and the page sends no
// system prompt of its own for one. From E3 on it goes through the signed-in axiosInstance, so
// the server can check that the student is placed in this condition (the backend's
// studyTutor/access.js; without a sign-in it checks the condition and its time only).

const STUDENT_CHAT_URL = process.env.REACT_APP_HOST + '/api/v1/gpt/studentChat'
const LEGACY_CHAT_URL = process.env.REACT_APP_HOST + '/api/v1/gpt/openAI'

// Elaborated replies can take far longer than axiosInstance's 10 s. The server gives each AI
// provider attempt 40 s and may retry once, or rewrite a reply that gave code away, so a slow
// provider can still outlast this wait (the student then sees the "couldn't answer" notice).
const STUDY_CHAT_TIMEOUT_MS = 90 * 1000

// Resolves to the same { role, content } object /gpt/openAI returns (a study condition's
// reply also carries the server's interactionId).
//   study: { userId, studyState } for a study condition's request, else omitted.
export const postStudentChat = async (session, messages, study = null) => {
  if (study) {
    const res = await axiosInstance.post(
      STUDENT_CHAT_URL,
      { sessionId: session?.id, messages, userId: study.userId, studyState: study.studyState },
      { timeout: STUDY_CHAT_TIMEOUT_MS }
    )
    return res.data
  }
  try {
    const res = await axios.post(STUDENT_CHAT_URL, { sessionId: session?.id, messages })
    return res.data
  } catch (error) {
    if (error?.response?.status !== 404) throw error
    const res = await axios.post(LEGACY_CHAT_URL, { messages })
    return res.data
  }
}

// A fresh id for one chat request: the server logs it with the exchange and the page's
// telemetry carries it too, so the two can be matched up. randomUUID needs a secure page
// (https or localhost); elsewhere a time-and-random id does the same job.
export const newClientRequestId = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
}

// The exception a run raised, by the traceback parser lastRunInfo's errorName comes from
// ("AttributeError: 'TaskList' object has no attribute 'tasks'" -> "AttributeError"), or
// null. TerminalContext empties terminalOutput when a run starts, so it holds the latest run
// alone.
const runErrorName = (terminalOutput) => {
  const logs = Array.isArray(terminalOutput) ? terminalOutput : []
  const failed = [...logs].reverse().find((log) => log?.type === 'stderr' && log.error)
  return failed ? pythonErrorName(failed.error.message || failed.content || '') : null
}

// What a study condition's request tells the server about the student's latest run, for the
// tutor's help-episode state and its log (studyState, without its clientRequestId):
// TerminalContext's lastRunInfo, or - on a page that has none - the failing tests read from
// testResults and the exception from the run's output, labelled and parsed as lastRunInfo's
// are (testResults.js), so the server compares like with like. codeHash and runId come only
// with lastRunInfo.
export const studyRunState = ({ lastRunInfo, testResults, testList, terminalOutput }) => {
  if (lastRunInfo && typeof lastRunInfo === 'object') {
    return {
      failingTests: Array.isArray(lastRunInfo.failingTests)
        ? lastRunInfo.failingTests.map(String)
        : failingTestLabels(testResults, testList),
      errorName: lastRunInfo.errorName ? String(lastRunInfo.errorName) : null,
      codeHash: lastRunInfo.codeHash ? String(lastRunInfo.codeHash) : null,
      runId: lastRunInfo.runId != null && lastRunInfo.runId !== '' ? String(lastRunInfo.runId) : null,
    }
  }
  return {
    failingTests: failingTestLabels(testResults, testList),
    errorName: runErrorName(terminalOutput),
    codeHash: null,
    runId: null,
  }
}
